import React, { useState, useEffect, Fragment, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { auth, db } from '../firebase-config'; // Adjust path as needed
import { onAuthStateChanged } from 'firebase/auth';
import {
    collection,
    query,
    where,
    // eslint-disable-next-line no-unused-vars
    onSnapshot,
    doc,
    updateDoc,
    Timestamp,
    arrayUnion,
    orderBy, // Added for sorting
    limit, // Added for pagination
    startAfter, // Added for pagination
    getDocs, // Needed for pagination count/next page logic
} from 'firebase/firestore';
import { Dialog, Transition, Switch } from '@headlessui/react';
import { ColorRing } from 'react-loader-spinner';
import { addDays, formatDistanceToNow } from 'date-fns';
import { fr as frLocale, enUS as enLocale } from 'date-fns/locale'; // Import locales for date-fns
import { FiCheckCircle, FiXCircle, FiAlertTriangle, FiInfo, FiX, FiChevronDown, FiChevronUp, FiExternalLink, FiRefreshCw } from 'react-icons/fi'; // Icons

// --- Constants ---
const SCRAPE_COLLECTION = 'linkedin_scrape_prospect';
const ITEMS_PER_PAGE = 10; // Pagination size

// --- Helper Functions ---
const formatTimestamp = (timestamp, locale) => {
    if (!timestamp) return 'N/A';
    const date = timestamp instanceof Timestamp ? timestamp.toDate() : new Date(timestamp);
    return formatDistanceToNow(date, { addSuffix: true, locale: locale === 'fr' ? frLocale : enLocale });
};

// --- Main Component ---
function ProspectVerificationPage() {
    const { t, i18n } = useTranslation("ProspectVerificationPage");
    const [currentUser, setCurrentUser] = useState(null);
    const [isAuthorized, setIsAuthorized] = useState(false);
    const [authLoading, setAuthLoading] = useState(true);
    const [prospects, setProspects] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [selectedProspect, setSelectedProspect] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [confirmation, setConfirmation] = useState({ // For confirmation dialog
        isOpen: false,
        action: null, // 'verify', 'reject'
        prospectId: null,
        prospectName: '',
    });
    const [updateLoading, setUpdateLoading] = useState(false); // Loading state for updates
    const [currentLanguage, setCurrentLanguage] = useState(i18n.language.split('-')[0]);

    // --- Pagination State ---
    const [lastVisible, setLastVisible] = useState(null); // Last document snapshot for pagination
    // eslint-disable-next-line no-unused-vars
    const [firstVisible, setFirstVisible] = useState(null); // First document snapshot for pagination (for previous page)
    const [currentPage, setCurrentPage] = useState(1);
    const [hasNextPage, setHasNextPage] = useState(true); // Assume there's a next page initially

    // --- Sorting State ---
    const [sortBy, setSortBy] = useState('lastHubspotSyncAttempt'); // Default sort field
    const [sortDirection, setSortDirection] = useState('desc'); // Default sort direction

    // --- Filtering State (Example) ---
    // eslint-disable-next-line no-unused-vars
    const [filterName, setFilterName] = useState(''); // Simple name filter example

    // --- Language Toggle ---
    const toggleLanguage = () => {
        const newLang = currentLanguage === 'en' ? 'fr' : 'en';
        i18n.changeLanguage(newLang);
        setCurrentLanguage(newLang);
        localStorage.setItem('i18nextLng', newLang); // Persist preference
    };

    // --- Authorization Effect ---
    useEffect(() => {
        const unsubscribe = onAuthStateChanged(auth, async (user) => {
            setAuthLoading(true);
            setIsAuthorized(false); // Reset authorization on user change
            if (user) {
                setCurrentUser({
                    uid: user.uid,
                    email: user.email,
                    displayName: user.displayName,
                });
                try {
                    const idTokenResult = await user.getIdTokenResult();
                    console.log("ID Token Result:", idTokenResult);
                    // Check for the custom claim
                    const claims = idTokenResult.claims.globalClaim || [];
                    console.log("User claims:", claims);
                    if (Array.isArray(claims) && claims.includes('marketing')) {
                        console.log("User is authorized (marketing claim found).");
                        setIsAuthorized(true);
                    } else {
                        console.warn("User is not authorized (missing 'marketing' claim).");
                        setError(t('error.unauthorized'));
                    }
                } catch (err) {
                    console.error("Error fetching custom claims:", err);
                    setError(t('error.authCheckFailed'));
                }
            } else {
                setCurrentUser(null);
                setError(t('error.notLoggedIn')); // Set error if user logs out
            }
            setAuthLoading(false);
        });
        return () => unsubscribe(); // Cleanup listener
    }, [t]); // Add t to dependencies

    // --- Firestore Data Fetching Effect ---
    /* const fetchProspects = useCallback((direction = 'next') => {
        if (!isAuthorized) {
            setLoading(false);
            return; // Don't fetch if not authorized
        }
        setLoading(true);
        setError(null);

        try {
            let q = query(
                collection(db, SCRAPE_COLLECTION),
                where('hubspotSync', '==', true), // Core filter
                orderBy(sortBy, sortDirection),
                limit(ITEMS_PER_PAGE)
            );

            // Apply filtering (simple example)
            // Note: Complex filtering might require composite indexes in Firestore
            // if (filterName) {
            //     q = query(q, where('fullName', '>=', filterName), where('fullName', '<=', filterName + '\uf8ff'));
            // }

            // Apply pagination cursor
            if (direction === 'next' && lastVisible) {
                console.log("Fetching next page with lastVisible:", lastVisible)
                q = query(q, startAfter(lastVisible));
            } else if (direction === 'prev' && firstVisible) {
                // Firestore doesn't directly support 'endBefore' with 'orderBy desc' easily for previous page.
                // A common workaround involves reversing sort order and using startAfter, then reversing results client-side.
                // For simplicity here, we'll just reset to page 1 on 'prev' if not implemented fully.
                // Or, store the firstVisible of the *previous* page to use startAfter.
                // Resetting to page 1 for this example:
                console.warn("Previous page functionality might require more complex query logic.");
                // Resetting:
                setLastVisible(null);
                setFirstVisible(null);
                setCurrentPage(1);
                 q = query(
                    collection(db, SCRAPE_COLLECTION),
                    where('hubspotSync', '==', true),
                    orderBy(sortBy, sortDirection),
                    limit(ITEMS_PER_PAGE)
                );
            }


            const unsubscribe = onSnapshot(q, async (querySnapshot) => {
                console.log(querySnapshot)
                if (querySnapshot.empty && currentPage > 1) {
                     console.log("No more documents found for the next page.");
                     setHasNextPage(false); // Reached the end
                     setLoading(false);
                     return; // Don't update prospects if the page is empty past page 1
                 } else if (querySnapshot.empty) {
                     console.log("No documents found matching the query.", SCRAPE_COLLECTION, currentPage, q, querySnapshot.docs.length, querySnapshot.docs);
                     setProspects([]); // Clear prospects if query is empty on page 1
                     setHasNextPage(false);
                 } else {
                    const prospectsData = querySnapshot.docs.map(doc => ({
                        id: doc.id,
                        ...doc.data()
                    }));
                    setProspects(prospectsData);
                    setFirstVisible(querySnapshot.docs[0]); // Store first doc of current page
                    setLastVisible(querySnapshot.docs[querySnapshot.docs.length - 1]); // Store last doc for next page

                    // Check if there's potentially a next page
                    const nextQuery = query(q, startAfter(querySnapshot.docs[querySnapshot.docs.length - 1]), limit(1));
                    const nextSnapshot = await getDocs(nextQuery);
                    setHasNextPage(!nextSnapshot.empty);
                 }

                setLoading(false);
            }, (err) => {
                console.error("Error fetching prospects:", err);
                setError(t('error.fetchFailed', { message: err.message }));
                setLoading(false);
            });

            return unsubscribe; // Return the unsubscribe function for cleanup

        } catch (err) {
            console.error("Error setting up Firestore listener:", err);
            setError(t('error.fetchSetupFailed', { message: err.message }));
            setLoading(false);
        }
    }, [isAuthorized, sortBy, sortDirection, lastVisible, firstVisible, currentPage]);  */// Add dependencies


    // --- Initial Fetch and Refetch on Sort/Filter Change ---
    /* useEffect(() => {
        // Reset pagination when sorting/filtering changes
        setLastVisible(null);
        setFirstVisible(null);
        setCurrentPage(1);
        const unsubscribe = fetchProspects(); // Fetch initial data or data based on new sort/filter
        return () => {
            if (unsubscribe) unsubscribe(); // Cleanup listener on sort/filter change or unmount
        };
    }, [isAuthorized, sortBy, sortDirection, filterName, fetchProspects]); */ // Re-run fetch when these change

    const fetchProspects = useCallback(async (direction = 'first', cursor = null) => {
        if (!isAuthorized) {
            setLoading(false);
            setProspects([]); // Clear prospects if not authorized
            return;
        }
        setLoading(true);
        setError(null);
    
        try {
            // Base query
            let q = query(
                collection(db, SCRAPE_COLLECTION),
                where('hubspotSync', '==', true),
                orderBy(sortBy, sortDirection),
                limit(ITEMS_PER_PAGE + 1) // Fetch one extra item
            );
    
            // Apply cursor for pagination
            if (direction === 'next' && cursor) {
                q = query(q, startAfter(cursor));
            }
            // NOTE: 'prev' direction with getDocs usually requires reversing orderBy,
            // using endBefore (if available/suitable) or startAfter with reversed sort,
            // and then reversing the results array.
            // The current implementation resets to first page on 'prev', so we might
            // not need specific 'prev' logic here if handlePrevPage resets state first.
            else if (direction === 'prev' && cursor) {
                 console.warn("True 'previous page' logic with getDocs is complex and not fully implemented in this example. Consider resetting to page 1 or implementing reverse query logic.");
                 // Example placeholder for complex prev logic:
                 // const reversedSortDir = sortDirection === 'asc' ? 'desc' : 'asc';
                 // q = query(collection(db, SCRAPE_COLLECTION), where(...), orderBy(sortBy, reversedSortDir), endBefore(cursor), limit(ITEMS_PER_PAGE + 1));
                 // Needs careful handling and result reversal.
                 // For now, assuming handlePrevPage resets state and calls fetchProspects('first').
            }
    
            const querySnapshot = await getDocs(q);
            const fetchedDocs = querySnapshot.docs;
    
            // Determine if there's a next page
            const hasMore = fetchedDocs.length > ITEMS_PER_PAGE;
    
            // Get the documents for the *current* page
            const currentPageDocs = hasMore ? fetchedDocs.slice(0, ITEMS_PER_PAGE) : fetchedDocs;
    
            const prospectsData = currentPageDocs.map(doc => ({
                id: doc.id,
                ...doc.data()
            }));
    
            setProspects(prospectsData);
            setFirstVisible(currentPageDocs.length > 0 ? currentPageDocs[0] : null);
            setLastVisible(currentPageDocs.length > 0 ? currentPageDocs[currentPageDocs.length - 1] : null);
            setHasNextPage(hasMore);
    
            // Reset if fetched data is empty but we weren't on page 1 (e.g., clicked next into an empty page)
            if (querySnapshot.empty && currentPage > 1) {
                 console.log("No documents found for this page.");
                 // Optionally, navigate back or show a message. Here, we just show no results.
                 setHasNextPage(false); // Definitely no next page
            } else if (querySnapshot.empty && currentPage === 1) {
                console.log("No documents found matching the initial query.");
                setHasNextPage(false);
            }
    
    
        } catch (err) {
            console.error("Error fetching prospects with getDocs:", err);
            setError(t('error.fetchFailed', { message: err.message }));
            setProspects([]); // Clear data on error
        } finally {
            setLoading(false);
        }
    // Dependencies focus on things that change the *query* itself (auth, sort, filter)
    // Pagination state (cursors, page number) is managed via arguments and component state updates.
    }, [isAuthorized, sortBy, sortDirection, ITEMS_PER_PAGE, t]); // Added t for error message
    
    
    // --- Update useEffect for initial load and sort/filter changes ---
    useEffect(() => {
        if (isAuthorized) {
            console.log("Effect running due to auth/sort/filter change. Resetting pagination and fetching page 1.");
            // Reset pagination state before fetching
            setLastVisible(null);
            setFirstVisible(null);
            setCurrentPage(1);
            fetchProspects('first'); // Fetch the initial page
        } else {
            // Handle case where user becomes unauthorized after being authorized
            setProspects([]);
            setLoading(false);
        }
        // No cleanup function needed here as getDocs is one-time
    }, [isAuthorized, sortBy, sortDirection, filterName, fetchProspects]); // fetchProspects dependency is okay here now
    
    // --- Update Pagination Handlers ---
    const handleNextPage = () => {
        if (hasNextPage && lastVisible) {
            console.log("Fetching next page after:", lastVisible.id);
            // Don't modify currentPage here, let fetchProspects update state based on results
            fetchProspects('next', lastVisible); // Pass the cursor
            setCurrentPage(prev => prev + 1); // Increment page number optimistically or after fetch success
        } else {
             console.log("Next page called but no next page or lastVisible missing", hasNextPage, lastVisible);
        }
    };
    
    const handlePrevPage = () => {
         if (currentPage > 1) {
             console.log("Fetching previous page (Resetting to Page 1)");
             // Strategy: Reset to page 1 (Simpler)
             setCurrentPage(1);
             setLastVisible(null);
             setFirstVisible(null);
             fetchProspects('first');
             // --- OR ---
             // Strategy: Implement complex 'prev' logic using firstVisible
             // console.log("Fetching previous page before:", firstVisible.id);
             // fetchProspects('prev', firstVisible); // Requires fetchProspects to handle 'prev' correctly
             // setCurrentPage(prev => prev - 1);
         }
    };
    
     // --- Update Refresh Handler ---
     const handleRefresh = () => {
         console.log("Refreshing list...");
         setCurrentPage(1);
         setLastVisible(null);
         setFirstVisible(null);
         fetchProspects('first');
     };
    
     // --- Update Refresh Button ---
     // Change the onClick in the JSX:
     /* onClick={handleRefresh} */ // Use the dedicated refresh handler

    // --- Modal Handlers ---
    const openModal = (prospect) => {
        setSelectedProspect(prospect);
        setIsModalOpen(true);
    };

    const closeModal = () => {
        setIsModalOpen(false);
        setSelectedProspect(null);
    };

    // --- Confirmation Handlers ---
    const showConfirmation = (action, prospectId, prospectName) => {
        setConfirmation({ isOpen: true, action, prospectId, prospectName });
    };

    const closeConfirmation = () => {
        setConfirmation({ isOpen: false, action: null, prospectId: null, prospectName: '' });
    };

    const handleConfirmAction = () => {
        if (confirmation.action === 'verify') {
            handleVerify(confirmation.prospectId);
        } else if (confirmation.action === 'reject') {
            handleReject(confirmation.prospectId);
        }
        closeConfirmation();
    };

    // --- Firestore Update Logic ---
    const updateProspectStatus = async (prospectId, action) => {
        if (!currentUser || !prospectId) {
            console.error("User or Prospect ID missing for update.");
            setError(t('error.updateMissingInfo'));
            return;
        }
        setUpdateLoading(true);
        setError(null); // Clear previous errors

        const docRef = doc(db, SCRAPE_COLLECTION, prospectId);
        const modificationData = {
            action: action, // 'verified', 'rejected'
            timestamp: Timestamp.now(), // Use server timestamp for accuracy
            user: {
                uid: currentUser.uid,
                email: currentUser.email,
                displayName: currentUser.displayName || currentUser.email, // Fallback to email if display name missing
            }
        };

        let updatePayload = {};
        if (action === 'verified') {
            const expiryDate = addDays(new Date(), 7);
            updatePayload = {
                expiryAt: Timestamp.fromDate(expiryDate),
                modifications: arrayUnion(modificationData) // Append to modifications array
            };
        } else if (action === 'rejected') {
            updatePayload = {
                hubspotSync: false, // Set back to false
                expiryAt: null, // Clear expiry date
                modifications: arrayUnion(modificationData)
            };
        } else {
            console.error("Invalid action type for update:", action);
            setUpdateLoading(false);
            return;
        }

        try {
            await updateDoc(docRef, updatePayload);
            console.log(`Prospect ${prospectId} successfully updated with action: ${action}`);
            // Close modal if the action was successful
            if (action === 'verified' || action === 'rejected') {
                closeModal();
            }
        } catch (err) {
            console.error(`Error updating prospect ${prospectId}:`, err);
            setError(t('error.updateFailed', { message: err.message }));
            // Optionally show error to user in modal or via toast
        } finally {
            setUpdateLoading(false);
        }
    };

    const handleVerify = (prospectId) => {
        updateProspectStatus(prospectId, 'verified');
    };

    const handleReject = (prospectId) => {
        updateProspectStatus(prospectId, 'rejected');
    };

    // --- Pagination Handlers ---
/*     const handleNextPage = () => {
        if (hasNextPage && lastVisible) {
            setCurrentPage(prev => prev + 1);
            fetchProspects('next'); // fetchProspects uses lastVisible state
        }
    };

    const handlePrevPage = () => {
         if (currentPage > 1) {
             setCurrentPage(prev => prev - 1);
             // Resetting to page 1 as full prev logic is complex
             setLastVisible(null);
             setFirstVisible(null);
             fetchProspects('first'); // Indicate fetching the first page
         }
    }; */

    // --- Sorting Handler ---
    const handleSort = (field) => {
        if (sortBy === field) {
            // Toggle direction if sorting by the same field
            setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
        } else {
            // Set new field and default to descending
            setSortBy(field);
            setSortDirection('desc');
        }
        // Reset pagination when sorting changes (handled by useEffect)
    };

    // --- Render Logic ---
    if (authLoading) {
        return (
            <div className="flex justify-center items-center h-screen">
                <ColorRing visible={true} height="80" width="80" ariaLabel="Authenticating..." colors={['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#6366f1']} />
                <p className="ml-4 text-slate-600 leading-relaxed">{t('loading.authenticating')}</p>
            </div>
        );
    }

    if (!isAuthorized) {
        return (
            <div className="flex flex-col justify-center items-center h-screen text-center p-4">
                <FiAlertTriangle className="text-red-500 text-6xl mb-4" />
                <h1 className="text-2xl font-semibold text-red-700 mb-2">{t('error.accessDenied')}</h1>
                <p className="text-slate-600 leading-relaxed">{error || t('error.noMarketingClaim')}</p>
                {/* Optionally add a logout button or link */}
            </div>
        );
    }

    return (
        <div className="container mx-auto p-4 md:p-6 lg:p-8">
            {/* Header and Language Toggle */}
            <div className="flex justify-between items-center mb-6">
                <h1 className="text-3xl font-bold text-slate-900 font-bold tracking-tight">{t('title')}</h1>
                <div className="flex items-center space-x-2">
                    <span className={`text-sm font-medium ${currentLanguage === 'en' ? 'text-slate-900 font-bold tracking-tight' : 'text-slate-500'}`}>EN</span>
                    <Switch
                        checked={currentLanguage === 'fr'}
                        onChange={toggleLanguage}
                        className={`${currentLanguage === 'fr' ? 'bg-[#A47713] hover:bg-[#8a6310]' : 'bg-gray-300'}
                          relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2`}
                        aria-label={t('toggleLanguage')}
                    >
                        <span
                            className={`${currentLanguage === 'fr' ? 'translate-x-6' : 'translate-x-1'}
                            inline-block h-4 w-4 transform rounded-full bg-white transition-transform`}
                        />
                    </Switch>
                    <span className={`text-sm font-medium ${currentLanguage === 'fr' ? 'text-slate-900 font-bold tracking-tight' : 'text-slate-500'}`}>FR</span>
                </div>
            </div>

            {/* Error Display */}
            {error && !loading && ( // Show persistent errors only when not loading
                <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded relative mb-4" role="alert">
                    <strong className="font-bold">{t('error.title')}: </strong>
                    <span className="block sm:inline">{error}</span>
                </div>
            )}

            {/* Controls (Filtering/Refresh - Example) */}
            <div className="mb-4 flex flex-wrap items-center gap-4">
                 {/* <div className="relative flex-grow">
                     <input
                         type="text"
                         placeholder={t('filterByName')}
                         value={filterName}
                         onChange={(e) => setFilterName(e.target.value)}
                         className="pl-8 pr-4 py-2 border rounded-md w-full focus:ring-blue-500 focus:border-blue-500"
                     />
                     <FiFilter className="absolute left-2 top-1/2 transform -translate-y-1/2 text-gray-400" />
                 </div> */}
                 <button
                    onClick={handleRefresh} // Refresh button resets to first page
                    className="flex items-center px-4 py-2 bg-blue-500 text-white rounded-md hover:bg-[#A47713] hover:bg-[#8a6310] focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50"
                    disabled={loading}
                    aria-label={t('refreshList')}
                 >
                     <FiRefreshCw className={`mr-2 ${loading ? 'animate-spin' : ''}`} />
                     {t('refresh')}
                 </button>
            </div>


            {/* Loading Indicator */}
            {loading && (
                <div className="flex justify-center items-center my-10">
                    <ColorRing visible={true} height="60" width="60" ariaLabel="Loading Prospects..." colors={['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#6366f1']} />
                     <p className="ml-4 text-slate-600 leading-relaxed">{t('loading.prospects')}</p>
                </div>
            )}

            {/* Prospects Table */}
            {!loading && prospects.length > 0 && (
                <div className="bg-white shadow-md rounded-lg overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">
                        <thead className="bg-gray-50">
                            <tr>
                                {/* Add sorting handlers to headers */}
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSort('fullName')}>
                                    {t('table.name')} {sortBy === 'fullName' && (sortDirection === 'asc' ? <FiChevronUp className="inline ml-1"/> : <FiChevronDown className="inline ml-1"/>)}
                                </th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSort('companyName')}>
                                    {t('table.company')} {sortBy === 'companyName' && (sortDirection === 'asc' ? <FiChevronUp className="inline ml-1"/> : <FiChevronDown className="inline ml-1"/>)}
                                </th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider">
                                    {t('table.title')}
                                </th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSort('location')}>
                                    {t('table.location')} {sortBy === 'location' && (sortDirection === 'asc' ? <FiChevronUp className="inline ml-1"/> : <FiChevronDown className="inline ml-1"/>)}
                                </th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-slate-500 uppercase tracking-wider cursor-pointer" onClick={() => handleSort('lastHubspotSyncAttempt')}>
                                    {t('table.synced')} {sortBy === 'lastHubspotSyncAttempt' && (sortDirection === 'asc' ? <FiChevronUp className="inline ml-1"/> : <FiChevronDown className="inline ml-1"/>)}
                                </th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {prospects.map((prospect) => (
                                <tr key={prospect.id} onClick={() => openModal(prospect)} className="hover:bg-gray-50 cursor-pointer transition-colors">
                                    <td className="px-6 py-4 whitespace-nowrap">
                                        <div className="text-sm font-medium text-slate-900 font-bold tracking-tight">{prospect.fullName || `${prospect.firstName} ${prospect.lastName}`}</div>
                                        <div className="text-sm text-slate-500">{prospect.email || prospect.workEmail || t('noEmail')}</div>
                                    </td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600 leading-relaxed">{prospect.companyName || t('notAvailable')}</td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600 leading-relaxed">{prospect.jobTitle || prospect.occupation || t('notAvailable')}</td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-600 leading-relaxed">{prospect.location || t('notAvailable')}</td>
                                    <td className="px-6 py-4 whitespace-nowrap text-sm text-slate-500">
                                        {formatTimestamp(prospect.lastHubspotSyncAttempt, currentLanguage)}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}

            {/* No Prospects Message */}
            {!loading && prospects.length === 0 && !error && (
                <div className="text-center py-10 text-slate-500">
                    <FiInfo className="text-4xl mx-auto mb-2" />
                    {t('noProspects')}
                </div>
            )}

             {/* Pagination Controls */}
             {!loading && prospects.length > 0 && (
                 <div className="mt-6 flex justify-between items-center">
                     <button
                         onClick={handlePrevPage}
                         disabled={currentPage <= 1}
                         className="px-4 py-2 text-sm font-medium text-slate-600 leading-relaxed bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                     >
                         {t('pagination.previous')}
                     </button>
                     <span className="text-sm text-slate-600 leading-relaxed">
                         {t('pagination.page', { currentPage })}
                     </span>
                     <button
                         onClick={handleNextPage}
                         disabled={!hasNextPage}
                         className="px-4 py-2 text-sm font-medium text-slate-600 leading-relaxed bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                     >
                         {t('pagination.next')}
                     </button>
                 </div>
             )}


            {/* Prospect Detail Modal */}
            <Transition appear show={isModalOpen} as={Fragment}>
                <Dialog as="div" className="relative z-10" onClose={closeModal}>
                    {/* Backdrop */}
                    <Transition.Child
                        as={Fragment}
                        enter="ease-out duration-300"
                        enterFrom="opacity-0"
                        enterTo="opacity-100"
                        leave="ease-in duration-200"
                        leaveFrom="opacity-100"
                        leaveTo="opacity-0"
                    >
                        <div className="fixed inset-0 bg-black bg-opacity-40" />
                    </Transition.Child>

                    {/* Modal Content */}
                    <div className="fixed inset-0 overflow-y-auto">
                        <div className="flex min-h-full items-center justify-center p-4 text-center">
                            <Transition.Child
                                as={Fragment}
                                enter="ease-out duration-300"
                                enterFrom="opacity-0 scale-95"
                                enterTo="opacity-100 scale-100"
                                leave="ease-in duration-200"
                                leaveFrom="opacity-100 scale-100"
                                leaveTo="opacity-0 scale-95"
                            >
                                <Dialog.Panel className="w-full max-w-3xl transform overflow-hidden rounded-2xl bg-white p-6 text-left align-middle shadow-xl transition-all">
                                    <Dialog.Title as="h3" className="text-xl font-semibold leading-6 text-slate-900 font-bold tracking-tight border-b pb-3 mb-4 flex justify-between items-center">
                                        {t('modal.title')} - {selectedProspect?.fullName}
                                         <button onClick={closeModal} className="text-gray-400 hover:text-slate-600 leading-relaxed" aria-label={t('modal.close')}>
                                             <FiX size={24}/>
                                         </button>
                                    </Dialog.Title>

                                    {/* Loading state for updates */}
                                    {updateLoading && (
                                        <div className="absolute inset-0 bg-white bg-opacity-75 flex justify-center items-center z-20">
                                            <ColorRing visible={true} height="50" width="50" ariaLabel="Updating..." colors={['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#6366f1']} />
                                        </div>
                                    )}

                                    {/* Prospect Data Display */}
                                    <div className="mt-2 max-h-[60vh] overflow-y-auto pr-2 text-sm text-slate-600 leading-relaxed">
                                        {/* Display all fields - use a definition list or similar for structure */}
                                        {selectedProspect && Object.entries(selectedProspect).map(([key, value]) => {
                                            // Skip complex objects/arrays for simple display, or format them
                                            if (typeof value === 'object' && value !== null && !Array.isArray(value) && !(value instanceof Timestamp)) {
                                                // Optionally render nested objects differently or skip
                                                // return <p key={key}><strong>{key}:</strong> [Object]</p>;
                                                return null; // Skip objects for now
                                            }
                                             if (Array.isArray(value) && value.length > 5) { // Truncate long arrays
                                                 return <p key={key}><strong>{key}:</strong> [Array ({value.length} items)]</p>;
                                             }

                                            let displayValue = value;
                                            if (value instanceof Timestamp) {
                                                displayValue = value.toDate().toLocaleString(currentLanguage);
                                            } else if (Array.isArray(value)) {
                                                displayValue = value.join(', '); // Simple join for arrays
                                            } else if (typeof value === 'boolean') {
                                                displayValue = value ? t('yes') : t('no');
                                            } else if (value === null || value === undefined || value === '') {
                                                displayValue = <span className="text-gray-400 italic">{t('notAvailable')}</span>;
                                            }

                                            // Add link for LinkedIn URL
                                            if ((key === 'linkedinUrl' || key === 'linkedinProfileUrl' || key === 'linkedinAccount') && typeof value === 'string' && value.startsWith('http')) {
                                                displayValue = (
                                                    <a href={value} target="_blank" rel="noopener noreferrer" className="text-blue-600 hover:underline inline-flex items-center">
                                                        {value} <FiExternalLink className="ml-1" size={12}/>
                                                    </a>
                                                );
                                            }


                                            return (
                                                <div key={key} className="grid grid-cols-3 gap-4 py-2 border-b border-gray-100 last:border-b-0">
                                                    <dt className="font-medium text-slate-600 leading-relaxed break-words">{key}</dt>
                                                    <dd className="col-span-2 text-slate-900 font-bold tracking-tight break-words">{displayValue}</dd>
                                                </div>
                                            );
                                        })}
                                    </div>

                                    {/* Modal Actions */}
                                    <div className="mt-6 pt-4 border-t flex flex-col sm:flex-row justify-end space-y-3 sm:space-y-0 sm:space-x-3">
                                        <button
                                            type="button"
                                            className="inline-flex justify-center rounded-md border border-transparent bg-red-100 px-4 py-2 text-sm font-medium text-red-900 hover:bg-red-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-red-500 focus-visible:ring-offset-2 disabled:opacity-50"
                                            onClick={() => showConfirmation('reject', selectedProspect?.id, selectedProspect?.fullName)}
                                            disabled={updateLoading}
                                        >
                                            <FiXCircle className="mr-2" />
                                            {t('modal.reject')}
                                        </button>
                                        <button
                                            type="button"
                                            className="inline-flex justify-center rounded-md border border-transparent bg-green-100 px-4 py-2 text-sm font-medium text-green-900 hover:bg-green-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-green-500 focus-visible:ring-offset-2 disabled:opacity-50"
                                            onClick={() => showConfirmation('verify', selectedProspect?.id, selectedProspect?.fullName)}
                                            disabled={updateLoading}
                                        >
                                            <FiCheckCircle className="mr-2" />
                                            {/* Change text based on whether expiryAt exists? */}
                                            {selectedProspect?.expiryAt ? t('modal.update') : t('modal.verify')}
                                        </button>
                                        <button
                                            type="button"
                                            className="inline-flex justify-center rounded-md border border-gray-300 bg-white px-4 py-2 text-sm font-medium text-slate-600 leading-relaxed hover:bg-gray-50 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 focus-visible:ring-offset-2 disabled:opacity-50"
                                            onClick={closeModal}
                                            disabled={updateLoading}
                                        >
                                            {t('modal.close')}
                                        </button>
                                    </div>
                                </Dialog.Panel>
                            </Transition.Child>
                        </div>
                    </div>
                </Dialog>
            </Transition>

             {/* Confirmation Modal */}
             <Transition appear show={confirmation.isOpen} as={Fragment}>
                 <Dialog as="div" className="relative z-20" onClose={closeConfirmation}>
                     <Transition.Child as={Fragment} /* Backdrop */ >
                         <div className="fixed inset-0 bg-black bg-opacity-30" />
                     </Transition.Child>
                     <div className="fixed inset-0 overflow-y-auto">
                         <div className="flex min-h-full items-center justify-center p-4 text-center">
                             <Transition.Child as={Fragment} /* Panel */ >
                                 <Dialog.Panel className="w-full max-w-md transform overflow-hidden rounded-lg bg-white p-6 text-left align-middle shadow-xl transition-all">
                                     <Dialog.Title as="h3" className="text-lg font-medium leading-6 text-slate-900 font-bold tracking-tight flex items-center">
                                         <FiAlertTriangle className={`mr-3 ${confirmation.action === 'verify' ? 'text-yellow-500' : 'text-red-500'}`} size={24} />
                                         {t('confirmation.title')}
                                     </Dialog.Title>
                                     <div className="mt-3">
                                         <p className="text-sm text-slate-600 leading-relaxed">
                                             {confirmation.action === 'verify'
                                                 ? t('confirmation.messageVerify', { name: confirmation.prospectName })
                                                 : t('confirmation.messageReject', { name: confirmation.prospectName })
                                             }
                                         </p>
                                     </div>
                                     <div className="mt-5 sm:mt-6 flex flex-col sm:flex-row justify-end space-y-3 sm:space-y-0 sm:space-x-3">
                                         <button
                                             type="button"
                                             className={`inline-flex justify-center w-full sm:w-auto rounded-md border border-transparent px-4 py-2 text-base font-medium text-white shadow-sm focus:outline-none focus:ring-2 focus:ring-offset-2 sm:text-sm ${confirmation.action === 'verify' ? 'bg-green-600 hover:bg-green-700 focus:ring-green-500' : 'bg-red-600 hover:bg-red-700 focus:ring-red-500'}`}
                                             onClick={handleConfirmAction}
                                         >
                                             {confirmation.action === 'verify' ? t('confirmation.confirmVerify') : t('confirmation.confirmReject')}
                                         </button>
                                         <button
                                             type="button"
                                             className="inline-flex justify-center w-full sm:w-auto rounded-md border border-gray-300 bg-white px-4 py-2 text-base font-medium text-slate-600 leading-relaxed shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 sm:text-sm"
                                             onClick={closeConfirmation}
                                         >
                                             {t('confirmation.cancel')}
                                         </button>
                                     </div>
                                 </Dialog.Panel>
                             </Transition.Child>
                         </div>
                     </div>
                 </Dialog>
             </Transition>

        </div>
    );
}

export default ProspectVerificationPage;

// --- Example firebase-config.js ---
// import { initializeApp } from "firebase/app";
// import { getAuth } from "firebase/auth";
// import { getFirestore } from "firebase/firestore";

// const firebaseConfig = {
//   apiKey: "YOUR_API_KEY",
//   authDomain: "YOUR_AUTH_DOMAIN",
//   projectId: "YOUR_PROJECT_ID", // e.g., interview-412415
//   storageBucket: "YOUR_STORAGE_BUCKET",
//   messagingSenderId: "YOUR_MESSAGING_SENDER_ID",
//   appId: "YOUR_APP_ID"
// };

// // Initialize Firebase
// const app = initializeApp(firebaseConfig);
// export const auth = getAuth(app);
// export const db = getFirestore(app);
